import React, { useEffect, useState } from 'react';
import { searchCentres, addToPortfolio, triggerPortfolioBenchmark } from '../api';

const CENTRE_TIERS = ['neighbourhood', 'community', 'regional', 'super_regional'];
const TIER_LABELS = { neighbourhood: 'Neighbourhood', community: 'Community', regional: 'Regional', super_regional: 'Super Regional' };

export default function PortfolioSetup({ onComplete, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tier, setTier] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    const timer = setTimeout(() => {
      setLoading(true);
      searchCentres(query, null, tier || null)
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [query, tier]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSave = async () => {
    if (selected.size === 0) return;
    setSaving(true);
    try {
      await addToPortfolio([...selected]);
      triggerPortfolioBenchmark().catch(() => {});
      setSaved(true);
    } catch (err) {
      console.error('Portfolio save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <div style={styles.overlay}>
        <div className="sq-dark-doc" style={styles.card}>
          <div style={styles.successIcon}>&#10003;</div>
          <div style={styles.title}>Portfolio Saved</div>
          <div style={styles.subtitle}>{selected.size} centre{selected.size !== 1 ? 's' : ''} added to your portfolio</div>
          <div style={styles.note}>Benchmarking is running in the background. Scores will appear on the Pipeline tab in a few minutes.</div>
          <button onClick={onComplete} style={styles.doneBtn}>Continue to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div className="sq-dark-doc" style={styles.panel}>
        <div style={styles.header}>
          <div>
            <div style={styles.title}>Select Your Properties</div>
            <div style={styles.subtitle}>Search for shopping centres in your portfolio</div>
          </div>
          {onClose && (
            <button onClick={onClose} style={styles.closeBtn}>&#10005;</button>
          )}
        </div>

        {/* Search */}
        <div style={styles.searchRow}>
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by centre name..."
            style={styles.searchInput}
            autoFocus
          />
        </div>

        {/* Tier filter */}
        <div style={styles.tierRow}>
          <button
            onClick={() => setTier('')}
            style={{ ...styles.tierBtn, ...(tier === '' ? styles.tierActive : {}) }}
          >All</button>
          {CENTRE_TIERS.map(t => (
            <button
              key={t}
              onClick={() => setTier(t)}
              style={{ ...styles.tierBtn, ...(tier === t ? styles.tierActive : {}) }}
            >{TIER_LABELS[t]}</button>
          ))}
        </div>

        {/* Results */}
        <div style={styles.resultsList}>
          {loading && <div style={styles.loadingText}>Searching...</div>}
          {!loading && query.length >= 2 && results.length === 0 && (
            <div style={styles.loadingText}>No centres found for "{query}"</div>
          )}
          {!loading && query.length < 2 && (
            <div style={styles.loadingText}>Type at least 2 characters to search</div>
          )}
          {results.map(c => {
            const isSelected = selected.has(c.id) || c.in_portfolio;
            return (
              <div
                key={c.id}
                onClick={() => !c.in_portfolio && toggle(c.id)}
                style={{ ...styles.centreCard, ...(isSelected ? styles.centreSelected : {}), ...(c.in_portfolio ? styles.centreOwned : {}) }}
              >
                <div style={styles.centreCheck}>
                  {isSelected ? '✓' : ''}
                </div>
                <div style={styles.centreInfo}>
                  <div style={styles.centreName}>{c.name}</div>
                  <div style={styles.centreMeta}>
                    {c.gla_sqm > 0 ? `${c.gla_sqm.toLocaleString()} sqm` : ''}{c.gla_sqm > 0 && c.centre_tier ? ' · ' : ''}
                    {TIER_LABELS[c.centre_tier] || c.centre_tier || ''}
                    {c.city ? ` · ${c.city}` : ''}
                    {c.poi_count ? ` · ${c.poi_count} tenants` : ''}
                  </div>
                  {c.in_portfolio && (
                    <div style={styles.ownedLabel}>Already in portfolio</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom bar */}
        <div style={styles.bottomBar}>
          <div style={styles.selectedCount}>
            {selected.size} centre{selected.size !== 1 ? 's' : ''} selected
          </div>
          <button
            onClick={handleSave}
            disabled={selected.size === 0 || saving}
            style={{ ...styles.saveBtn, opacity: selected.size > 0 && !saving ? 1 : 0.5 }}
          >
            {saving ? 'Saving...' : `Save Portfolio (${selected.size})`}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 16 },
  panel: { background: 'var(--white)', borderRadius: 10, width: '100%', maxWidth: 640, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  card: { background: 'var(--white)', borderRadius: 10, padding: '40px 32px', maxWidth: 420, width: '100%', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' },
  header: { padding: '20px 24px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
  title: { fontFamily: "'News Cycle',serif", fontSize: 22, fontWeight: 700, color: 'var(--ink)' },
  subtitle: { fontFamily: "'DM Mono',monospace", fontSize: 11, color: 'var(--muted)', letterSpacing: '0.5px', marginTop: 2 },
  closeBtn: { background: 'transparent', border: 'none', fontSize: 18, color: 'var(--muted)', cursor: 'pointer', padding: '4px 8px' },
  searchRow: { padding: '12px 24px' },
  searchInput: { width: '100%', padding: '10px 14px', borderRadius: 6, border: '1px solid var(--rule)', background: 'var(--cream)', fontSize: 14, fontFamily: "'Work Sans',sans-serif", outline: 'none', boxSizing: 'border-box' },
  tierRow: { display: 'flex', gap: 4, padding: '0 24px 12px', flexWrap: 'wrap' },
  tierBtn: { padding: '4px 10px', background: 'transparent', border: '1px solid var(--rule)', borderRadius: 14, fontSize: 10, color: 'var(--muted)', cursor: 'pointer', fontFamily: "'DM Mono',monospace" },
  tierActive: { background: 'var(--accent)', color: '#14110e', borderColor: 'var(--accent)' },
  resultsList: { flex: 1, overflowY: 'auto', padding: '0 24px', minHeight: 200, maxHeight: 400 },
  loadingText: { textAlign: 'center', padding: '30px 0', color: 'var(--muted)', fontSize: 13 },
  centreCard: { display: 'flex', gap: 12, alignItems: 'center', padding: '10px 12px', borderRadius: 6, border: '1px solid var(--rule)', marginBottom: 6, cursor: 'pointer', background: 'var(--white)' },
  centreSelected: { borderColor: 'var(--accent)', background: 'rgba(255,106,61,0.08)' },
  centreOwned: { opacity: 0.5, cursor: 'default' },
  centreCheck: { width: 24, height: 24, borderRadius: 4, border: '2px solid var(--rule)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: 'var(--accent)', fontWeight: 700, flexShrink: 0 },
  centreInfo: { flex: 1, minWidth: 0 },
  centreName: { fontSize: 13, fontWeight: 600, color: 'var(--ink)' },
  centreMeta: { fontFamily: "'DM Mono',monospace", fontSize: 10, color: 'var(--muted)', marginTop: 2 },
  ownedLabel: { fontFamily: "'DM Mono',monospace", fontSize: 9, color: 'var(--green)', marginTop: 2 },
  bottomBar: { padding: '12px 24px', borderTop: '1px solid var(--rule)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  selectedCount: { fontFamily: "'DM Mono',monospace", fontSize: 11, color: 'var(--muted)' },
  saveBtn: { padding: '10px 20px', background: 'var(--accent)', color: '#14110e', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'Work Sans',sans-serif" },
  successIcon: { width: 48, height: 48, borderRadius: '50%', background: 'rgba(26,122,74,0.1)', color: 'var(--green)', fontSize: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  note: { fontSize: 12, color: 'var(--muted)', lineHeight: 1.6, maxWidth: 340 },
  doneBtn: { padding: '12px 24px', background: 'var(--accent)', color: '#14110e', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'Work Sans',sans-serif" },
};
